import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Users,
  Award,
  Target,
  Heart,
  Truck,
  Shield,
  Clock,
  Home,
  ChevronRight,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import WebsiteHeader from "./WebsiteHeader";
import WebsiteFooter from "./WebsiteFooter";

const WebsiteAbout = () => {
  const values = [
    {
      icon: Heart,
      title: "Customer First",
      description: "Your satisfaction is our priority. We go the extra mile to ensure you find exactly what you need.",
    },
    {
      icon: Award,
      title: "Quality Products",
      description: "We stock only trusted brands and quality materials that stand the test of time.",
    },
    {
      icon: Target,
      title: "Expert Knowledge",
      description: "Our team has years of experience in hardware and building supplies to guide your projects.",
    },
    {
      icon: Shield,
      title: "Trust & Integrity",
      description: "Honest pricing, reliable service, and standing behind every product we sell.",
    },
  ];

  const features = [
    "Wide range of building materials",
    "Quality tools and equipment",
    "Plumbing and electrical supplies",
    "Paint and finishing products",
    "Garden and outdoor equipment",
    "Fencing and security products",
    "Friendly expert advice",
    "Competitive pricing",
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <WebsiteHeader />
      
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/site" className="text-gray-500 hover:text-blue-600 flex items-center gap-1">
              <Home className="h-4 w-4" />
              Home
            </Link>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="text-gray-800 font-medium">About Us</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                About <span className="text-blue-400">BOSVELD</span>
              </h1>
              <p className="text-xl text-gray-300 mb-6 max-w-xl">
                Your trusted hardware partner in Tom Burke. We have been serving the local community 
                with quality building materials, tools, and expert advice.
              </p>
              <div className="flex flex-wrap gap-6 justify-center md:justify-start">
                <div className="text-center">
                  <p className="text-4xl font-bold text-blue-400">10+</p>
                  <p className="text-sm text-gray-400">Years Experience</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-blue-400">5000+</p>
                  <p className="text-sm text-gray-400">Products</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-blue-400">1000+</p>
                  <p className="text-sm text-gray-400">Happy Customers</p>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <img 
                src="/logo.jpg" 
                alt="BOSVELD BOU & HARDEWARE" 
                className="h-48 md:h-64 rounded-xl shadow-2xl bg-white p-4"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <p className="text-lg text-gray-600 mb-6">
              BOSVELD BOU & HARDEWARE has been a cornerstone of the Tom Burke community, 
              providing quality hardware, building materials, and expert advice to homeowners, 
              contractors, and businesses alike.
            </p>
            <p className="text-lg text-gray-600 mb-6">
              What started as a small hardware store has grown into a comprehensive supplier 
              of everything you need for construction, renovation, and maintenance projects. 
              Our commitment to quality products and exceptional customer service has earned 
              us the trust of our community.
            </p>
            <p className="text-lg text-gray-600">
              Whether you are building a new home, renovating your kitchen, or just need 
              a few screws for a weekend project, we are here to help with friendly service 
              and expert advice.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-lg text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
                    <value.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-6">What We Offer</h2>
              <p className="text-lg text-gray-600 mb-8">
                We stock a comprehensive range of products to meet all your building, 
                renovation, and maintenance needs. From foundation to finish, we have 
                got you covered.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 grid grid-cols-2 gap-4">
              <Card className="bg-blue-50 border-0">
                <CardContent className="p-6 text-center">
                  <Truck className="h-10 w-10 mx-auto mb-2 text-blue-600" />
                  <p className="font-medium">Local Delivery</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-0">
                <CardContent className="p-6 text-center">
                  <Users className="h-10 w-10 mx-auto mb-2 text-green-600" />
                  <p className="font-medium">Expert Staff</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-0">
                <CardContent className="p-6 text-center">
                  <Shield className="h-10 w-10 mx-auto mb-2 text-purple-600" />
                  <p className="font-medium">Quality Brands</p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-0">
                <CardContent className="p-6 text-center">
                  <Clock className="h-10 w-10 mx-auto mb-2 text-orange-600" />
                  <p className="font-medium">6 Days a Week</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-xl mx-auto">
            Visit us today or browse our products online. We are here to help make your 
            project a success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/site/products">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                Browse Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/site/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export default WebsiteAbout;
